import React, { FunctionComponent } from 'react';
import { Appear, Card, CardContent, Flex, Icon, Link, SkeletonBox, Text } from '@bitrise/bitkit';
import { CrashEvent } from '@trace/types';

import dataAtIds from '../../../../dataAtIds.json';
import CrashDetailsDivider from './CrashDetailsDivider';
import CrashException from './CrashException';
import CrashThreads from './CrashThreads';

export interface Props {
  crashEvent?: CrashEvent;
  isError: boolean;
  isFetching: boolean;
  onRefetch: () => void;
}

const CrashDetails: FunctionComponent<Props> = ({ crashEvent, isError, isFetching, onRefetch }) => {
  if (isError) {
    return (
      <Flex alignChildrenVertical="middle" direction="vertical" grow>
        <Appear animation="Fade" textColor="red-3">
          <Flex alignChildren="middle" direction="vertical" height="100%">
            <Flex>
              <Flex alignChildren="middle" direction="vertical">
                <Icon margin="x1" name="BitbotFailed" size="3rem" />
              </Flex>
              <Text align="middle" ellipsis>
                Uh oh, there&apos;s been a problem.
              </Text>
              <Text align="middle">
                {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                <Link
                  clickable
                  data-trace-at-id={dataAtIds.CrashPageDetailsErrorRetry}
                  onClick={onRefetch}
                  underline
                >
                  Click here to retry
                </Link>
                .
              </Text>
            </Flex>
          </Flex>
        </Appear>
      </Flex>
    );
  }

  if (!isFetching && !crashEvent) {
    return (
      <Flex
        alignChildrenVertical="middle"
        data-trace-at-id={dataAtIds.CrashPageDetailsNoDataMessage}
        direction="vertical"
        grow
      >
        <Appear animation="Fade">
          <Text align="middle">There are no details to display for this event</Text>
        </Appear>
      </Flex>
    );
  }

  const { platform = 'ios' } = crashEvent?.attributes || {};
  const crashedThread = crashEvent?.threads.find(({ crashed }) => crashed);
  const nonCrashedThreads = crashEvent?.threads.filter(({ crashed }) => !crashed);

  return (
    <Card
      data-trace-at-id={dataAtIds.CrashPageEventDetails}
      direction="vertical"
      gap="x8"
      paddingVertical="x8"
    >
      <CardContent>
        <Text config="5" margin="x5" paddingHorizontal="x8">
          Exceptions
        </Text>

        <CrashDetailsDivider />

        {isFetching ? (
          <Flex
            data-trace-at-id={dataAtIds.CrashPageDetailsLoadingIndicator}
            paddingHorizontal="x8"
            paddingVertical="x4"
            direction="vertical"
            gap="x1"
          >
            <SkeletonBox height="1.125rem" />
            <SkeletonBox height="1.125rem" />
            <SkeletonBox height="1.125rem" />
          </Flex>
        ) : (
          crashedThread && (
            <CrashException
              platform={platform}
              title={crashEvent?.title}
              subtitle={crashEvent?.description}
              thread={crashedThread}
              expandByDefault
            />
          )
        )}

        <CrashDetailsDivider />
      </CardContent>

      <CardContent>
        <Text config="5" margin="x5" paddingHorizontal="x8">
          Threads
        </Text>

        <CrashDetailsDivider />

        {isFetching ? (
          <Flex
            data-trace-at-id={dataAtIds.CrashPageDetailsLoadingIndicator}
            paddingHorizontal="x8"
            paddingVertical="x4"
            direction="vertical"
            gap="x1"
          >
            <SkeletonBox height="1.125rem" />
            <SkeletonBox height="1.125rem" />
            <SkeletonBox height="1.125rem" />
          </Flex>
        ) : (
          crashedThread && (
            <CrashException
              platform={platform}
              title={crashEvent?.title}
              subtitle={crashEvent?.description}
              thread={crashedThread}
            />
          )
        )}

        {isFetching ? (
          <Flex
            data-trace-at-id={dataAtIds.CrashPageDetailsLoadingIndicator}
            paddingHorizontal="x8"
            paddingVertical="x4"
            direction="vertical"
            gap="x1"
          >
            <SkeletonBox height="1.125rem" />
            <SkeletonBox height="1.125rem" />
            <SkeletonBox height="1.125rem" />
          </Flex>
        ) : (
          nonCrashedThreads &&
          nonCrashedThreads.length > 0 && (
            <>
              <CrashDetailsDivider />
              <CrashThreads platform={platform} threads={nonCrashedThreads} />
            </>
          )
        )}

        <CrashDetailsDivider />
      </CardContent>
    </Card>
  );
};

export default CrashDetails;
